import React, { useContext, useEffect, useState } from 'react'
import {
  LOGIN_JOURNEY,
  LOGIN_TITLE,
  FORGEROCK_LOGIN_STAGES,
  ENVIRONMENT_VARIABLES,
  FR_URL,
} from '~utils/constants'
import { checkPingTokenExpiry, initAuth } from '~utils/forgerock'
import Pingform from '~components/Pingform/Pingform'
import CustomSkeletonLoader from '~components/CustomSkeletonLoader/CustomSkeletonLoader'
import { Helmet } from 'react-helmet'
import OTPCollectForm from '~components/shared/OTPCollectForm/OTPCollectForm'
import { IUserContextType, UserContext } from '~utils/context/UserContext'
import { FRLoginSuccess, StepType } from '@forgerock/javascript-sdk'
import OTPChannelSelectForm from '~components/shared/OTPChannelSelectForm/OTPChannelSelectForm'
import UnknownError from '~components/UnknownError/UnknownError'
import UserNamePasswordForm from '~components/UserNamePasswordForm/UserNamePasswordForm'
import UserNameForm from '~components/UserNameForm/UserNameForm'
import PasswordForm from '~components/PasswordForm/PasswordForm'
import DeviceInput from '~components/shared/DeviceInput/DeviceInput'
import { useLocation } from 'react-router-dom'
import ResetPassword from '~components/shared/ResetPassword/ResetPassword'
import SessionExpire from '~components/SessionExpire/SessionExpire'

const LoginJourney = () => {
  const {
    step,
    setStep,
    nextStep,
    userInputError,
    isLoading,
    storeToken,
    frSessionTimeout,
    setStoreToken,
    otpSupport,
    env,
    showErr,
  } = useContext(UserContext) as IUserContextType
  const location = useLocation()
  const token = storeToken ? storeToken : location?.state?.storeToken
  const [stage, setStage] = useState<string | undefined>('')

  useEffect(() => {
    const initializeJourney = async () => {
      if (token && env) {
        setStoreToken(token)
        initAuth(LOGIN_JOURNEY, token, ENVIRONMENT_VARIABLES[env][FR_URL])
        nextStep(undefined)
      } else {
        setStep(undefined)
      }
    }
    initializeJourney()
  }, [token, env])

  useEffect(() => {
    if (!otpSupport) {
      setStage(undefined)
    }
  }, [nextStep])

  useEffect(() => {
    if (step?.type !== FRLoginSuccess) {
      setStage(step?.payload?.stage)
    } else {
      setStage(undefined)
    }
  }, [step, userInputError, showErr])

  const stepComponent = () => {
    //const newStage = stage
    if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_USERNAME_PASSWORD) {
      return <UserNamePasswordForm key="UserNamePasswordForm" />
    } else if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_DEVICE_PROFILE && showErr) {
      return <UserNamePasswordForm key="UserNamePasswordForm" />
    } else if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_DEVICE_PROFILE && !showErr) {
      return <DeviceInput key="deviceInput" />
    } else if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_OTP_DELIVERY_CHANNEL && !otpSupport) {
      return <OTPChannelSelectForm key="OTPChannelSelectForm" />
    } else if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_COLLECT_OTP || otpSupport) {
      return <OTPCollectForm key="OTPCollectForm" />
    } else if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_RESET_PASSWORD) {
      return <ResetPassword key="ResetPasswordForm" />
    } else if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_USERNAME) {
      return <UserNameForm key="UserNameForm" />
    } else if (stage === FORGEROCK_LOGIN_STAGES.LOGIN_PASSWORD) {
      return <PasswordForm key="PasswordForm" />
    } else if (showErr) {
      return <SessionExpire />
    } else {
      return
    }
  }

  return isLoading ? (
    <CustomSkeletonLoader />
  ) : (
    <>
      <Helmet>
        <title>{LOGIN_TITLE}</title>
      </Helmet>

      {(step === undefined ||
        (step?.type === StepType.Step &&
          typeof stage === 'string' &&
          !Object.values(FORGEROCK_LOGIN_STAGES).includes(stage))) &&
        !checkPingTokenExpiry() &&
        !frSessionTimeout && <UnknownError />}

      {(step === undefined || step?.type === StepType.Step) &&
        (checkPingTokenExpiry() || frSessionTimeout) && <SessionExpire />}

      {step?.type === StepType.Step &&
        !checkPingTokenExpiry() &&
        !frSessionTimeout &&
        stepComponent()}

      {step?.type === StepType.LoginSuccess && <Pingform step={step} />}

      {step?.payload?.message && <div>{step?.payload?.message}</div>}
    </>
  )
}

export default LoginJourney
